import { useCallback, useEffect, useRef, useState } from 'react';
import { checkSalesCart, getSalesAvailability, SalesAvailability, SalesLocation, salesCartStockError, salesStockError } from '../../services/sales-ordering/sales-delivery.service';

// Each result belongs to an exact location/cart snapshot. Late responses never unblock a changed cart.
export function useSalesDelivery(visible: boolean, actorId: string, location: SalesLocation, items: Array<{ id: number; quantity: number }>) {
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify([visible, actorId, location.userId, location.userAddressId, location.isSelfPickup, revision]);
  const itemsKey = JSON.stringify(items.map(i => [i.id, i.quantity]).sort((a, b) => a[0] - b[0]));
  const cartKey = JSON.stringify([key, itemsKey]);
  const current = useRef(cartKey); current.current = cartKey;
  const [snapshot, setSnapshot] = useState<{ key: string; data?: SalesAvailability; error?: string }>();
  const [checked, setChecked] = useState<{ key: string; error?: string }>();
  const [verifying, setVerifying] = useState(false);
  const ready = visible && !!actorId && (location.isSelfPickup || (!!location.userId && location.userAddressId > 0));
  useEffect(() => {
    const controller = new AbortController();
    if (ready) getSalesAvailability(location, controller.signal).then(data => { if (!controller.signal.aborted) setSnapshot({ key, data }); })
      .catch(error => { if (!controller.signal.aborted) setSnapshot({ key, error: salesStockError(error) }); });
    return () => controller.abort();
    // location fields are included in key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);
  const data = ready && snapshot?.key === key ? snapshot.data ?? null : null;
  const hasQuantities = data?.productStock !== undefined;
  useEffect(() => {
    const controller = new AbortController();
    // New APIs supply the entire location balance. Cart edits can be checked
    // immediately against it; checkout still performs a fresh server check.
    // Older APIs retain a debounced check, never an unchecked checkout.
    const timer = ready && data && !hasQuantities && items.length ? window.setTimeout(() => {
      checkSalesCart(location, items, controller.signal).then(() => { if (!controller.signal.aborted) setChecked({ key: cartKey }); })
        .catch(error => { if (!controller.signal.aborted) setChecked({ key: cartKey, error: salesStockError(error) }); });
    }, 250) : undefined;
    return () => { window.clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey, ready, data, hasQuantities]);
  useEffect(() => {
    const refresh = () => { if (visible) setRevision(n => n + 1); };
    const storage = (event: StorageEvent) => { if (event.key === null || event.key === 'pincodePosMappingsChanged' || event.key === 'customerDeliveryChanged') refresh(); };
    window.addEventListener('focus', refresh); window.addEventListener('pincodePosMappingsChanged', refresh); window.addEventListener('storage', storage);
    return () => { window.removeEventListener('focus', refresh); window.removeEventListener('pincodePosMappingsChanged', refresh); window.removeEventListener('storage', storage); };
  }, [visible]);
  const error = !ready ? 'Select a customer delivery address or choose Self Pickup to check stock.' :
    snapshot?.key === key ? snapshot.error : undefined;
  const cartError = data && hasQuantities ? salesCartStockError(items, data) : checked?.key === cartKey ? checked.error : undefined;
  const availabilityLoading = ready && !error && !data;
  const pending = verifying || (ready && !error && (!data || (!hasQuantities && items.length > 0 && checked?.key !== cartKey)));
  const verify = useCallback(async () => {
    const before = cartKey;
    if (!data || error) throw new Error(error || 'Wait for stock availability to finish, then retry.');
    setVerifying(true);
    try {
      await checkSalesCart(location, items);
      if (current.current !== before) throw new Error('The address or cart changed. Review it and continue again.');
      return before;
    } catch (failure) {
      if (current.current === before) setRevision(n => n + 1);
      throw failure;
    } finally { setVerifying(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey, data, error]);
  return { data, error: error || cartError, pending, availabilityLoading, verify, key: cartKey, current,
    refresh: () => setRevision(n => n + 1), blocked: !data || pending || !!error || !!cartError };
}
